import { useEffect, useId, useState } from 'react';
import { chatRequest } from '../../service/ChatService';
import { usePropertyReferences } from './usePropertyReferences';
import './property-reference.css';
const requestProperties = (path, signal) => chatRequest(path, { signal });
export function withPropertyReference(body, reference) {
    if (!reference)
        return body;
    const prefix = `Entire property · Not job-specific\nProperty: ${reference.address.trim() || `Property #${reference.propertyId}`}`;
    return body.trim() ? `${prefix}\n\n${body}` : prefix;
}
export function PropertyReferenceCard({ reference, disabled, onRemove }) {
    return <div className="property-reference-card"><div><strong>Entire property</strong><span>{reference.address || `Property #${reference.propertyId}`}</span></div><button type="button" disabled={disabled} onClick={onRemove} aria-label="Remove property reference">×</button></div>;
}
export function usePropertyReferencePicker(text, scope, enabled, input, onSelect) {
    const [caret, setCaret] = useState(0);
    const suggestions = usePropertyReferences(requestProperties, text, caret, scope, enabled);
    const [selection, setSelection] = useState({ key: '', index: 0 });
    const key = `${scope}:${suggestions.token?.query}`;
    const index = Math.min(selection.key === key ? selection.index : 0, Math.max(0, suggestions.rows.length - 1));
    const id = useId();
    useEffect(() => { document.getElementById(`${id}-${index}`)?.scrollIntoView?.({ block: 'nearest' }); }, [id, index]);
    const select = (property) => {
        const token = suggestions.token;
        if (!token)
            return;
        onSelect(text.slice(0, token.start) + text.slice(token.end), { kind: 'PROPERTY', propertyId: property.id, address: property.full_address });
        suggestions.dismiss();
        requestAnimationFrame(() => { input.current?.focus({ preventScroll: true }); input.current?.setSelectionRange(token.start, token.start); setCaret(token.start); });
    };
    const onKeyDown = (event) => {
        if (!suggestions.token || event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229)
            return false;
        if (event.key === 'Escape') {
            event.preventDefault();
            suggestions.dismiss();
            return true;
        }
        if (suggestions.rows.length && ['ArrowDown', 'ArrowUp', 'Enter', 'Tab'].includes(event.key) && !event.shiftKey) {
            event.preventDefault();
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp')
                setSelection({ key, index: (index + (event.key === 'ArrowDown' ? 1 : suggestions.rows.length - 1)) % suggestions.rows.length });
            else if (!event.repeat)
                select(suggestions.rows[index]);
            return true;
        }
        return false;
    };
    const menu = suggestions.token ? <div className="property-reference-picker" id={id} role="listbox" aria-label="Property suggestions">
    {suggestions.rows.map((row, i) => <button key={row.id} id={`${id}-${i}`} type="button" role="option" aria-selected={index === i} onMouseDown={event => event.preventDefault()} onClick={() => select(row)}>{row.full_address}</button>)}
    {suggestions.loading ? <span role="status">Loading properties…</span> : null}
    {suggestions.error ? <div role="alert">{suggestions.error} <button type="button" onClick={suggestions.retry}>Retry</button></div> : !suggestions.loading && !suggestions.rows.length ? <span>No properties found.</span> : null}
    {suggestions.more ? <button type="button" disabled={suggestions.loading} onClick={suggestions.loadMore}>More properties</button> : null}
    <button type="button" onClick={suggestions.dismiss}>Dismiss suggestions</button>
  </div> : null;
    return { menu, onKeyDown, setCaret, inputProps: { 'aria-autocomplete': 'list', 'aria-controls': suggestions.token ? id : undefined, 'aria-activedescendant': suggestions.rows.length ? `${id}-${index}` : undefined } };
}
